import { Button, Menu, Tooltip } from "@mantine/core";
import { IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { sortRowsByDateTime } from "src/generator/utils/sortByDateTime";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { setSections, toggleSortOrder } from "src/redux/slices/generatorSlice";
import { insertValues } from "src/redux/utils/insertValues";

/**
 * Sorts rows chronologically by date first (if present), then by time.
 * Handles grouped rows with the same draggableId by sorting based on the date/time-containing row
 * and keeping grouped rows together. Also handles subrows structure.
 */

interface Props {
  hamburger?: boolean;
}

const SortEntriesButton = ({ hamburger }: Props) => {
  const dispatch = useDispatch();
  const { lbentry, sortOrder } = useSelector(s => s.generatorSlice);
  const { getValues } = useFormContext();

  const isSortToggleable = lbentry.sortToggleable === true;

  // Sorts a section's rows and keeps savedRows in the same order as the sorted rows.
  const sortSection = (section: any, isAscending: boolean) => {
    const originalRows: any[] = section.rows;
    const sortedRows = sortRowsByDateTime(
      originalRows,
      isAscending,
      section.dynamicRows || section.cloneRows,
    );

    let savedRows = section.savedRows;
    if (savedRows && savedRows.length > 0) {
      // Build an index map: row reference → original savedRows value
      const savedByRow = new Map<any, boolean>(
        originalRows.map((row, i) => [row, savedRows[i] ?? false]),
      );
      savedRows = sortedRows.map(row => savedByRow.get(row) ?? false);
    }

    return { ...section, rows: sortedRows, savedRows };
  };

  const handleButtonClick = useCallback(() => {
    if (isSortToggleable) {
      // Toggle sort order first, then sort
      dispatch(toggleSortOrder());

      // Get current form values and insert them into sections
      const filledSections = insertValues(getValues(), lbentry.sections);

      // Use the opposite of current sort order (since we just toggled)
      const isAscending = sortOrder === "desc"; // We toggled above, so desc means next will be asc

      dispatch(
        setSections(filledSections.map(s => sortSection(s, isAscending))),
      );
    } else {
      // Just sort ascending (not toggleable)
      const filledSections = insertValues(getValues(), lbentry.sections);

      dispatch(setSections(filledSections.map(s => sortSection(s, true))));
    }
    dispatch(setEditing(true));
  }, [dispatch, getValues, lbentry.sections, sortOrder, isSortToggleable]);

  const Component = useCallback(() => {
    const SortIcon =
      sortOrder === "desc" ? IconSortAscending : IconSortDescending;

    const buttonTooltip = isSortToggleable
      ? sortOrder === "asc"
        ? "Sort newest to oldest"
        : "Sort oldest to newest"
      : "Sort oldest to newest";

    const buttonText = isSortToggleable
      ? sortOrder === "asc"
        ? "Sort Newest First"
        : "Sort Oldest First"
      : "Sort";

    return hamburger ? (
      <Menu.Item
        onClick={handleButtonClick}
        leftSection={<SortIcon size={20} />}>
        {buttonText}
      </Menu.Item>
    ) : (
      <Tooltip label={buttonTooltip} withArrow>
        <Button
          onClick={handleButtonClick}
          variant="outline"
          w={36}
          h={36}
          p={0}
          ml={15}>
          <SortIcon size={18} />
        </Button>
      </Tooltip>
    );
  }, [hamburger, handleButtonClick, sortOrder, isSortToggleable]);

  // Only render if isBridgeLog is true
  if (!isSortToggleable) {
    return null; // Disabled due to sort toggle should be explicitly turned on backend
  }

  return <Component />;
};

export default SortEntriesButton;
